<script lang="ts">
	import ProductCardCartOptions from "@components/ProductCardCartOptions.svelte";
	import ProductCardMedia from "@components/ProductCardMedia.svelte";
	import ProductCardMediaFallback from "@components/ProductCardMediaFallback.svelte";
	import ProductTags from "@components/ProductTags.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTagsForProductCard } from "@components/getTagsForProductCard.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import plus from "@core/assets/icons/plus.svg?raw";
	import type { OrderProduct } from "@core/schema/OrderProduct.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CartOverviewProduct.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const { orderProduct }: { orderProduct: OrderProduct } = $props();
	const { tenant, subcategories } = getGlobalTemplateProps();
	const product = $derived(Cart.findProduct(orderProduct));
	const subcategory = $derived(subcategories.find(({ id }) => id === product?.subcategoryId));
	const primaryMedia = $derived(product && getProductPrimaryMedia(product, orderProduct.info.attributes));

	function removeFromCart(): void {
		Cart.removeProduct(orderProduct);
	}

	const link = $derived(
		product &&
			subcategory &&
			getProductDetailPath(
				tenant.locale,
				product,
				subcategory,
				orderProduct.info.variant,
				orderProduct.info.attributes,
			),
	);
	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, orderProduct.info));
</script>

<div class="grid grid-cols-[6rem_auto] sm:grid-cols-[8rem_auto] xl:grid-cols-[10rem_auto] gap-y-2 gap-x-4 sm:gap-x-6">
	<div class="w-full aspect-square row-span-1 sm:row-span-2 bg-fcrlightgray-400 rounded-2xl overflow-hidden relative">
		<a href={link} class="absolute inset-0 size-full cursor-pointer" aria-hidden="true">
			{#if primaryMedia}
				<ProductCardMedia mediaSrc={primaryMedia} alt={heading} />
			{:else}
				<ProductCardMediaFallback />
			{/if}
		</a>
		{#if product}
			<div class="absolute z-10 inset-1.5 sm:inset-2 lg:inset-3 pointer-events-none">
				<ProductTags
					tags={getTagsForProductCard({
						// Prevent the new tag to appear
						product: { ...product, markedAsNewAt: undefined },
						locale: tenant.locale,
						subcategory,
					})}
				/>
			</div>
		{/if}
	</div>
	<div class="sm:border-t border-fcrlightgray pt-2 2xl:pt-6 flex flex-col justify-center sm:justify-between">
		<div class="flex justify-between">
			<div class="text-fcrdarkgray">
				<div class="font-antipol font-medium text-base lg:text-lg 2xl:text-1.5xl/8">
					{#if link}
						<span class="transition-colors hover:text-fcrbrown [div:has(>a:hover)+div_&]:text-fcrbrown">
							<Formatted text="[{link}][[{heading}]]" />
						</span>
					{:else}
						<Formatted text={heading} />
					{/if}
				</div>
				<div class="text-3 sm:text-3.5 xl:text-base">
					{#if Cart.currency}
						<Formatted
							text={translate("perPiece", {
								price: formatPrice(orderProduct.info.price.withTax, Cart.currency, tenant.locale, true),
							})}
						/>
					{/if}
				</div>
			</div>
			<button
				type="button"
				class={[
					"hidden sm:flex border-fcrgray-300 border size-12 items-center justify-center rounded-full",
					"[&:not(:disabled):hover]:border-fcrdarkgray ml-1",
					"[&:not(:disabled):hover:active]:scale-95 transition-transform duration-150 ease-in-out",
				]}
				onclick={removeFromCart}
				aria-label={translate("removeFromCart")}
			>
				<Icon icon={plus} class="size-5 2xl:size-6 rotate-45" />
			</button>
		</div>
	</div>
	{#if product}
		<div
			class="w-full sm:border-b border-fcrlightgray sm:pb-2 2xl:pb-6 col-span-2 sm:col-span-1 flex items-end justify-between"
		>
			<div
				class={[
					"rounded-full bg-white border border-fcrgray-100/24 px-4 py-2 text-fcrdarkgray text-sm/8 font-medium",
					{
						invisible: product.variantType === ProductVariantType.None,
					},
				]}
			>
				{product.variant.label[tenant.locale]}
			</div>
			<ProductCardCartOptions
				{product}
				showToasts={false}
				shrinkOnMobile
				max={getProductStockAmount(product.stock, product.variant, orderProduct.info.attributes)}
				attributes={orderProduct.info.attributes}
			/>
		</div>
	{/if}
</div>
